import { FC, useState, useEffect, useMemo } from "react";
import { Room } from "../../../models/Room";
import { RoomToken } from "../../../models/RoomToken";
import { RoomItem } from "../../../models/RoomItem";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { Util } from "../../../services/Utils/Util";
import { Trans, t } from "@lingui/macro";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";

import { useToast } from "../../../components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Copy, Check, Zap, Eye, EyeOff, Loader2, RefreshCw, ShieldCheck, File as FileIcon, Plus, X, BarChart3 } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { doc, collection, getDoc } from "firebase/firestore";

function getAvailableScopes() {
  return [
    { key: 'message:send', label: t`Send messages`, description: t`Send messages to this channel via API` },
    { key: 'files:read', label: t`Read files`, description: t`Access file list in this channel` },
    { key: 'memos:read', label: t`Read memos`, description: t`Access memo list in this channel` },
  ];
}

function getFileExtension(filename: string | undefined): string {
  if (!filename) return '';
  const parts = filename.split('.');
  if (parts.length < 2) return '';
  const ext = parts[parts.length - 1].toUpperCase();
  return ext.length <= 4 ? ext : ext.substring(0, 4);
}

function getExtensionColor(ext: string): string {
  const colors: Record<string, string> = {
    'PDF': 'bg-destructive', 'DOC': 'bg-blue-500', 'DOCX': 'bg-blue-500',
    'XLS': 'bg-green-600', 'XLSX': 'bg-green-600', 'PPT': 'bg-primary', 'PPTX': 'bg-primary',
    'ZIP': 'bg-yellow-600', 'RAR': 'bg-yellow-600', 'MP3': 'bg-purple-500',
    'MP4': 'bg-pink-500', 'MOV': 'bg-pink-500',
  };
  return colors[ext] || 'bg-muted-foreground';
}

const FileThumbnail: FC<{ item: RoomItem }> = ({ item }) => {
  const isImage = item.url && Util.isImageUrl(item.url);
  if (isImage) {
    return (
      <div className="w-9 h-9 rounded-md overflow-hidden bg-muted shrink-0">
        <img src={item.url} className="w-full h-full object-cover" alt={item.title} loading="lazy" />
      </div>
    );
  }
  const filename = item.title || item.file?.filename;
  const ext = getFileExtension(filename);
  const bgColor = getExtensionColor(ext);
  return (
    <div className="relative w-9 h-9 flex items-center justify-center rounded-md bg-muted shrink-0">
      <FileIcon className="w-5 h-5 text-muted-foreground" />
      {ext && (
        <span className={cn("absolute bottom-0 left-0 right-0 text-center text-on-media-text font-bold rounded-b text-meta-sm px-0.5", bgColor)}>
          {ext}
        </span>
      )}
    </div>
  );
};

// ─── 사용량 차트 컴포넌트 ─────────────────────────────

const UsageChartTab: FC<{ tokenId: string; monthlyQuota: number; currentMonthUsage: number }> = ({ tokenId, monthlyQuota, currentMonthUsage }) => {
  const [dayFieldMap, setDayFieldMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const currentMonth = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    (async () => {
      try {
        // roomtokens/{tokenId}/daily_usage/{YYYY-MM} 직접 경로
        const monthDocRef = doc(Fire.store, RoomToken.COL, tokenId, 'daily_usage', currentMonth);
        console.log('[UsageChart] Reading:', monthDocRef.path);
        const monthDoc = await getDoc(monthDocRef);
        console.log('[UsageChart] exists:', monthDoc.exists(), 'data:', monthDoc.data());
        if (monthDoc.exists()) {
          setDayFieldMap(monthDoc.data() as Record<string, number>);
        }
      } catch (e) {
        console.error('Failed to load usage data:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [tokenId, currentMonth]);

  const monthData = useMemo(() => {
    const now = new Date();
    const today = now.getDate();
    const days: { date: string; count: number; cumulative: number }[] = [];
    let cumulative = 0;

    for (let d = 1; d <= today; d++) {
      const dayKey = String(d).padStart(2, '0'); // "01" ~ "31"
      const count = dayFieldMap[dayKey] || 0;
      cumulative += count;
      days.push({ date: String(d), count, cumulative });
    }
    return days;
  }, [dayFieldMap, currentMonth]);

  const usagePercent = monthlyQuota > 0 ? Math.min(100, (currentMonthUsage / monthlyQuota) * 100) : 0;
  const isOverLimit = currentMonthUsage >= monthlyQuota;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* 사용량 요약 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground"><Trans>Monthly usage</Trans></span>
          <span className={cn("font-semibold tabular-nums", isOverLimit ? "text-destructive" : "text-[color:rgb(var(--text-body))]")}>
            {currentMonthUsage.toLocaleString()} / {monthlyQuota.toLocaleString()}
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all duration-500", isOverLimit ? "bg-destructive" : "bg-[color:rgb(var(--badge-api-bg))]")}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
        {isOverLimit && (
          <p className="text-xs text-destructive font-medium">
            <Trans>Monthly quota exceeded. API calls are blocked until next month.</Trans>
          </p>
        )}
      </div>

      {/* 일별 막대그래프 */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground"><Trans>Daily usage</Trans></Label>
        <div className="h-[160px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgb(var(--text-muted-foreground))' }} />
              <YAxis tick={{ fontSize: 10, fill: 'rgb(var(--text-muted-foreground))' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'rgb(var(--popover))', border: '1px solid rgb(var(--border))', borderRadius: 8, fontSize: 12 }}
                labelFormatter={(v) => `${currentMonth}-${String(v).padStart(2, '0')}`}
              />
              <Bar dataKey="count" fill="rgb(var(--badge-api-bg))" radius={[3, 3, 0, 0]} name={t`Calls`} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 누적 면적 그래프 */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground"><Trans>Cumulative usage</Trans></Label>
        <div className="h-[160px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgb(var(--text-muted-foreground))' }} />
              <YAxis tick={{ fontSize: 10, fill: 'rgb(var(--text-muted-foreground))' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'rgb(var(--popover))', border: '1px solid rgb(var(--border))', borderRadius: 8, fontSize: 12 }}
                labelFormatter={(v) => `${currentMonth}-${String(v).padStart(2, '0')}`}
              />
              <ReferenceLine y={monthlyQuota} stroke="rgb(var(--destructive))" strokeDasharray="6 3" label={{ value: t`Limit`, position: 'right', fontSize: 10, fill: 'rgb(var(--destructive))' }} />
              <Area type="monotone" dataKey="cumulative" stroke="rgb(var(--badge-api-bg))" fill="rgb(var(--badge-api-bg))" fillOpacity={0.15} name={t`Cumulative`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ─── 메인 모달 ─────────────────────────────────────────

interface TokenCreateModalProps {
  room: Room;
  open: boolean;
  onClose: () => void;
  editToken?: RoomToken;
}

export const TokenCreateModal: FC<TokenCreateModalProps> = ({ room, open, onClose, editToken }) => {
  const isEdit = !!editToken;
  const [name, setName] = useState(editToken?.name || "");
  const [isActive, setIsActive] = useState(editToken?.isActive ?? true);
  const [isSaving, setIsSaving] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isRegenerated, setIsRegenerated] = useState(false);
  
  // 권한 스코프 및 공개 범위
  const [scopes, setScopes] = useState<string[]>(editToken?.scopes ?? ['message:send']);
  const [filesVisibility, setFilesVisibility] = useState<'all' | 'selected'>(editToken?.filesVisibility ?? 'all');
  const [memosVisibility, setMemosVisibility] = useState<'all' | 'selected'>(editToken?.memosVisibility ?? 'all');
  const [visibleFileIds, setVisibleFileIds] = useState<string[]>(editToken?.visibleFileIds ?? []);
  const [visibleMemoIds, setVisibleMemoIds] = useState<string[]>(editToken?.visibleMemoIds ?? []);
  
  // IP 허용 목록
  const [allowedIps, setAllowedIps] = useState<string[]>(editToken?.allowedIps ?? []);
  
  const [files, setFiles] = useState<RoomItem[]>([]);
  const [memos, setMemos] = useState<RoomItem[]>([]);
  const [viewMode, setViewMode] = useState<'main' | 'files' | 'memos'>('main');
  const [search, setSearch] = useState("");
  
  // 탭: 설정 / 사용량
  const [activeTab, setActiveTab] = useState<'settings' | 'usage'>('settings');

  useEffect(() => {
    if (open && room?.id) {
      Fire.item.getRoomItems(room.id, ['file']).then(setFiles);
      Fire.item.getRoomItems(room.id, ['memo']).then(setMemos);
    }
  }, [open, room?.id]);

  // editToken 변경 시 allowedIps 동기화
  useEffect(() => {
    if (editToken) {
      setAllowedIps(editToken.allowedIps ?? []);
    }
  }, [editToken]);


  const { toast } = useToast();
  const availableScopes = getAvailableScopes();

  function toggleScope(scope: string) {
    setScopes(prev => {
      const isAdding = !prev.includes(scope);
      if (!isAdding) return prev.filter(s => s !== scope);

      if (scope === 'message:send') {
        return ['message:send'];
      } else {
        return [...prev.filter(s => s !== 'message:send'), scope];
      }
    });
  }

  // IP 행 관리
  function addIpRow() {
    setAllowedIps(prev => [...prev, '']);
  }
  function removeIpRow(index: number) {
    setAllowedIps(prev => prev.filter((_, i) => i !== index));
  }
  function updateIpRow(index: number, value: string) {
    setAllowedIps(prev => prev.map((ip, i) => i === index ? value.trim() : ip));
  }

  async function handleSave() {
    if (!name.trim()) {
      toast({ title: t`Please enter a name`, duration: 1500 });
      return;
    }

    if (scopes.length === 0) {
      toast({ title: t`At least one permission is required`, duration: 1500 });
      return;
    }

    // 빈 IP 행 필터 (공백만 있는 행 제거)
    const cleanedIps = allowedIps.filter(ip => ip.trim().length > 0);

    setIsSaving(true);
    try {
      if (isEdit && editToken) {
        // 편집 모드
        const updateData: Record<string, any> = {
          name: name.trim(),
          isActive,
          scopes,
          filesVisibility: scopes.includes('files:read') ? filesVisibility : 'all',
          memosVisibility: scopes.includes('memos:read') ? memosVisibility : 'all',
          visibleFileIds: scopes.includes('files:read') && filesVisibility === 'selected' ? visibleFileIds : [],
          visibleMemoIds: scopes.includes('memos:read') && memosVisibility === 'selected' ? visibleMemoIds : [],
          allowedIps: cleanedIps,
        };


        await Fire.token.updateToken(room.id, editToken.id, updateData);
        toast({ title: t`Token updated` });
        onClose();
      } else {
        // 생성 모드
        const result = await Fire.token.createToken(
          room.id,
          name.trim(),
          My.user.id,
          null,
          scopes,
          scopes.includes('files:read') ? filesVisibility : 'all',
          scopes.includes('memos:read') ? memosVisibility : 'all',
          scopes.includes('files:read') && filesVisibility === 'selected' ? visibleFileIds : [],
          scopes.includes('memos:read') && memosVisibility === 'selected' ? visibleMemoIds : []
        );

        // 생성된 토큰에 allowedIps 추가 저장
        if (cleanedIps.length > 0) {
          await Fire.token.updateToken(room.id, result.token.id, { allowedIps: cleanedIps });
        }

        // 평문 키 표시
        setCreatedSecret(result.plainSecret);
        setShowSecret(true);
      }
    } catch (e) {
      console.error("Token save error:", e);
      toast({ title: t`Failed to save token`, duration: 2000 });
    } finally {
      setIsSaving(false);
    }
  }



  async function handleCopySecret() {
    if (createdSecret) {
      await navigator.clipboard.writeText(createdSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleClose() {
    setCreatedSecret(null);
    setShowSecret(false);
    setIsRegenerated(false);
    setViewMode('main');
    setActiveTab('settings');
    setSearch("");
    onClose();
  }

  async function handleRegenerate() {
    if (!editToken) return;
    setIsRegenerating(true);
    try {
      const newSecret = await Fire.token.regenerateSecret(editToken.id);
      setCreatedSecret(newSecret);
      setShowSecret(true);
      setIsRegenerated(true);
      toast({ title: t`API key regenerated` });
    } catch (e) {
      console.error('Regenerate error:', e);
      toast({ title: t`Failed to regenerate key`, duration: 2000 });
    } finally {
      setIsRegenerating(false);
    }
  }

  // 시크릿 키 생성 완료 화면
  if (createdSecret) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-[color:rgb(var(--badge-api-bg))]" />
              {isRegenerated ? t`Key regenerated` : t`Token created`}
            </DialogTitle>
            <DialogDescription>
              <Trans>Copy the API key now. You won't be able to see it again.</Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {/* API 키 표시 영역 */}
            <div className="relative">
              <div className="bg-muted rounded-lg p-4 font-mono text-sm break-all select-all border border-border">
                {showSecret ? createdSecret : '•'.repeat(36)}
              </div>
              <div className="absolute top-2 right-2 flex gap-1">
                <Button
                  variant="icon-panel"
                  onClick={() => setShowSecret(!showSecret)}
                  title={showSecret ? t`Hide` : t`Show`}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <Button
              onClick={handleCopySecret}
              className="w-full"
              variant={copied ? "outline" : "default"}
            >
              {copied ? (
                <><Check className="w-4 h-4 mr-2" /><Trans>Copied!</Trans></>
              ) : (
                <><Copy className="w-4 h-4 mr-2" /><Trans>Copy API Key</Trans></>
              )}
            </Button>

            {/* API 문서 링크 */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted border border-border">
              <Zap className="w-5 h-5 text-[color:rgb(var(--badge-api-bg))] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[color:rgb(var(--text-body))]">
                  <Trans>API Documentation</Trans>
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <Trans>View endpoints, examples, and test your integration</Trans>
                </p>
              </div>
              <a
                href={
                  import.meta.env.VITE_MAIN_DOMAIN === 'app.nextpod.jp'
                    ? 'https://nextpod-api.web.app'
                    : 'https://nextpod-api-dev.web.app'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-[color:rgb(var(--badge-api-bg))] text-on-primary-text hover:opacity-90 transition-opacity"
              >
                <Trans>Open</Trans> →
              </a>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleClose}>
              <Trans>Done</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // 생성/편집 폼
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {viewMode === 'main' && (
      <DialogContent className="sm:max-w-[460px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[color:rgb(var(--badge-api-bg))]" />
            {isEdit ? t`Edit token` : t`Create API token`}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t`Modify token settings`
              : t`Create a token for external systems to send messages to this channel`
            }
          </DialogDescription>
        </DialogHeader>

        {/* 탭 (편집 모드에서만 사용량 탭 표시) */}
        {isEdit && (
          <div className="flex gap-1 mt-1 p-1 rounded-lg bg-muted">
            <button
              onClick={() => setActiveTab('settings')}
              className={cn(
                "flex-1 py-1.5 text-xs font-medium rounded-md transition-colors",
                activeTab === 'settings'
                  ? "bg-background text-[color:rgb(var(--text-body))] shadow-sm"
                  : "text-muted-foreground hover:text-[color:rgb(var(--text-body))]"
              )}
            >
              <Trans>Settings</Trans>
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={cn(
                "flex-1 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1.5",
                activeTab === 'usage'
                  ? "bg-background text-[color:rgb(var(--text-body))] shadow-sm"
                  : "text-muted-foreground hover:text-[color:rgb(var(--text-body))]"
              )}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              <Trans>Usage</Trans>
            </button>
          </div>
        )}

        {/* 설정 탭 */}
        {activeTab === 'settings' && (
        <div className="mt-2 space-y-5">
          {/* 이름 입력 */}
          <div>
            <Label htmlFor="token-name"><Trans>Name</Trans></Label>
            <Input
              id="token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t`e.g. GachiAI integration`}
              maxLength={30}
              className="mt-1"
            />
          </div>

          {/* 권한 스코프 */}
          <div>
            <Label className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              <Trans>Permissions</Trans>
              {isEdit && <span className="text-meta-sm text-muted-foreground font-normal ml-1">(<Trans>Cannot be modified</Trans>)</span>}
            </Label>
            <div className={cn("mt-2 space-y-2", isEdit && "opacity-60 pointer-events-none")}>
              {availableScopes.map((scope) => (
                <div key={scope.key} className="flex flex-col gap-2 p-2.5 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                  <label className={cn("flex items-center gap-3", isEdit ? "cursor-default" : "cursor-pointer")}>
                    <input
                      type="checkbox"
                      checked={scopes.includes(scope.key)}
                      onChange={() => toggleScope(scope.key)}
                      disabled={isEdit}
                      className="w-4 h-4 rounded accent-[color:rgb(var(--badge-api-bg))]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{scope.label}</div>
                      <div className="text-xs text-muted-foreground">{scope.description}</div>
                    </div>
                  </label>
                  
                  {scopes.includes(scope.key) && scope.key === 'files:read' && (
                    <div className="ml-7 pl-3 border-l-2 border-border flex flex-col gap-2">
                      <div className="text-meta-sm font-medium text-muted-foreground"><Trans>Files visibility</Trans></div>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input type="radio" name="filesVis" checked={filesVisibility === 'all'} onChange={() => setFilesVisibility('all')} className="accent-[color:rgb(var(--badge-api-bg))]" />
                          <Trans>All files in channel</Trans>
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                          <input type="radio" name="filesVis" checked={filesVisibility === 'selected'} onChange={() => setFilesVisibility('selected')} className="accent-[color:rgb(var(--badge-api-bg))]" />
                          <Trans>Selected files</Trans>
                        </label>
                      </div>
                      {filesVisibility === 'selected' && (
                        <div className="mt-2 text-xs flex items-center justify-between bg-muted/50 p-2 rounded-md border border-border">
                          <span className="text-muted-foreground mr-2 font-medium">{visibleFileIds.length} <Trans>files selected</Trans></span>
                          <Button type="button" variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => { setViewMode('files'); setSearch(""); }}>
                            <Trans>Select files...</Trans>
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {scopes.includes(scope.key) && scope.key === 'memos:read' && (
                    <div className="ml-7 pl-3 border-l-2 border-border flex flex-col gap-2">
                      <div className="text-meta-sm font-medium text-muted-foreground"><Trans>Memos visibility</Trans></div>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input type="radio" name="memosVis" checked={memosVisibility === 'all'} onChange={() => setMemosVisibility('all')} className="accent-[color:rgb(var(--badge-api-bg))]" />
                          <Trans>All memos in channel</Trans>
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                          <input type="radio" name="memosVis" checked={memosVisibility === 'selected'} onChange={() => setMemosVisibility('selected')} className="accent-[color:rgb(var(--badge-api-bg))]" />
                          <Trans>Selected memos</Trans>
                        </label>
                      </div>
                      {memosVisibility === 'selected' && (
                        <div className="mt-2 text-xs flex items-center justify-between bg-muted/50 p-2 rounded-md border border-border">
                          <span className="text-muted-foreground mr-2 font-medium">{visibleMemoIds.length} <Trans>memos selected</Trans></span>
                          <Button type="button" variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => { setViewMode('memos'); setSearch(""); }}>
                            <Trans>Select memos...</Trans>
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* IP 허용 목록 (옵션) */}
          <div>
            <Label className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              <Trans>Allowed IPs</Trans>
              <span className="text-meta-sm text-muted-foreground font-normal ml-1">(<Trans>Optional</Trans>)</span>
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              <Trans>Only requests from these IPs will be accepted. Leave empty to allow all.</Trans>
            </p>
            <div className="space-y-2">
              {allowedIps.map((ip, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={ip}
                    onChange={(e) => updateIpRow(index, e.target.value)}
                    placeholder={t`e.g. 123.45.67.89`}
                    className="flex-1 font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="icon-ghost"
                    onClick={() => removeIpRow(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addIpRow}
                className="w-full text-xs"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                <Trans>Add IP</Trans>
              </Button>
            </div>
          </div>

          {/* 활성/비활성 토글 (편집 모드에서만) */}
          {isEdit && (
            <div className="flex items-center justify-between">
              <div>
                <Label><Trans>Active</Trans></Label>
                <p className="text-xs text-muted-foreground">
                  <Trans>When inactive, the token cannot send messages</Trans>
                </p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          )}

          {/* 키 재발급 (편집 모드에서만) */}
          {isEdit && (
            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <Label><Trans>API Key</Trans></Label>
                  <p className="text-xs text-muted-foreground">
                    <Trans>Generate a new key. The old key will be invalidated.</Trans>
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={isRegenerating}
                >
                  {isRegenerating
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <RefreshCw className="w-4 h-4 mr-1.5" />
                  }
                  <Trans>Regenerate</Trans>
                </Button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* 사용량 탭 */}
        {activeTab === 'usage' && isEdit && editToken && (
          <div className="mt-2">
            <UsageChartTab
              tokenId={editToken.id}
              monthlyQuota={editToken.monthlyQuota ?? 10000}
              currentMonthUsage={editToken.currentMonthUsage ?? 0}
            />
          </div>
        )}

        {/* 하단 버튼 */}
        {activeTab === 'settings' && (
        <DialogFooter className="mt-6">
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !name.trim() || scopes.length === 0}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
      )}

      {viewMode === 'files' && (
        <DialogContent className="sm:max-w-md h-[80vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0 flex items-center justify-between flex-row space-y-0">
            <DialogTitle><Trans>Select files</Trans></DialogTitle>
          </DialogHeader>
          <div className="px-4 py-3 border-b flex-shrink-0 flex items-center gap-2">
            <Input placeholder={t`Search files...`} value={search} onChange={e => setSearch(e.target.value)} className="h-9" />
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-1.5 scrollbar-thin">
            {files.filter(f => (f.title || f.url || "").toLowerCase().includes(search.toLowerCase())).map(f => (
              <label key={f.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 border border-transparent hover:border-border cursor-pointer transition-colors">
                <input 
                  type="checkbox" 
                  checked={visibleFileIds.includes(f.id)}
                  onChange={(e) => {
                    if (e.target.checked) setVisibleFileIds(prev => [...prev, f.id]);
                    else setVisibleFileIds(prev => prev.filter(id => id !== f.id));
                  }}
                  className="w-4 h-4 accent-[color:rgb(var(--badge-api-bg))]"
                />
                <FileThumbnail item={f} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate text-[color:rgb(var(--text-body))]">{f.title || f.url}</div>
                  <div className="text-meta-sm text-muted-foreground truncate font-medium">{f.user?.name}</div>
                </div>
              </label>
            ))}
            {files.length === 0 && (
              <div className="text-center py-10 text-muted-foreground text-sm"><Trans>No files in this channel.</Trans></div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
            <Button onClick={() => setViewMode('main')} className="w-full sm:w-auto"><Trans>Done</Trans></Button>
          </DialogFooter>
        </DialogContent>
      )}

      {viewMode === 'memos' && (
        <DialogContent className="sm:max-w-md h-[80vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0 flex items-center justify-between flex-row space-y-0">
            <DialogTitle><Trans>Select memos</Trans></DialogTitle>
          </DialogHeader>
          <div className="px-4 py-3 border-b flex-shrink-0 flex items-center gap-2">
            <Input placeholder={t`Search memos...`} value={search} onChange={e => setSearch(e.target.value)} className="h-9" />
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-1.5 scrollbar-thin">
            {memos.filter(m => (m.title || m.text || "").toLowerCase().includes(search.toLowerCase())).map(m => (
              <label key={m.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 border border-transparent hover:border-border cursor-pointer transition-colors">
                <input 
                  type="checkbox" 
                  checked={visibleMemoIds.includes(m.id)}
                  onChange={(e) => {
                    if (e.target.checked) setVisibleMemoIds(prev => [...prev, m.id]);
                    else setVisibleMemoIds(prev => prev.filter(id => id !== m.id));
                  }}
                  className="w-4 h-4 accent-[color:rgb(var(--badge-api-bg))]"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate text-[color:rgb(var(--text-body))]">{m.title || m.text?.substring(0, 30) || "Untitled"}</div>
                  <div className="text-meta-sm text-muted-foreground truncate font-medium">{m.user?.name}</div>
                </div>
              </label>
            ))}
            {memos.length === 0 && (
              <div className="text-center py-10 text-muted-foreground text-sm"><Trans>No memos in this channel.</Trans></div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
            <Button onClick={() => setViewMode('main')} className="w-full sm:w-auto"><Trans>Done</Trans></Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};
